<!-- Copyright (c) 2023 Digital Systems (M) Sdn. Bhd. -->
<template>
  <div class="warehouse-main">
    <Menus :menu="menus" @menuClicked="menuClicked" />
    <div class="right-container">
      <TableBox
        :tableData="warehouseData"
        title="Warehouse Product"
        :selectedRow.sync="selectedWarehouseProduct"
        :dataType="{ 'Job DateTime': 'date' }"
      />
    </div>

    <PopupEntry
      v-if="popupEntryShow"
      :show.sync="popupEntryShow"
      :title="popupEntryTitle"
      :createdBy="createdBy"
      :updatedBy="updatedBy"
      :requiredError="entryRequired"
      @requiredClicked="entryRequiredClicked"
      :successMessages="entrySuccess"
      :messages="entryMessages"
      :errorMessages="entryError"
      :viewMode="viewMode"
      @save="saveClicked"
    >
      <div class="code-section" ref="code-section">
        <Input
          placeholder="Product ID"
          :isRequired="true"
          :inputValue.sync="prodID"
          :isDisabled="true"
          :errorMessage.sync="prodIDError"
          ref="productID"
          style="margin-left: 5px; flex: 1"
          validator="integer,10"
        />
        <Input
          placeholder="Product Code"
          :isRequired="true"
          :inputValue.sync="prodCode"
          :isDisabled="viewMode"
          :errorMessage.sync="prodCodeError"
          ref="productCode"
          style="margin-left: 5px; flex: 2"
          validator="text,50,uppercase"
          @focusChanged="handleRepeatedProductCode"
        />
        <Input
          placeholder="Product Name"
          :isRequired="true"
          :inputValue.sync="prodName"
          :isDisabled="viewMode"
          :errorMessage.sync="prodNameError"
          ref="productName"
          style="margin-left: 5px; flex: 2"
          validator="text,100,uppercase"
        />
      </div>

      <Tab
        style="height: calc(100% - 83px)"
        :tabs="tabsItems"
        :tabIndex.sync="currentTab"
        @tabClicked="tabClicked"
        :preventTabSwitch="true"
      >
        <div slot="General Details">
          <div class="warehouseproduct-general-main">
            <InputQuery
              placeholder="Item Type"
              :items="itemType"
              itemKey="Item Type"
              descKey="Description"
              :selectedObject.sync="selectedItemType"
              :isDisabled="viewMode"
              ref="itemType"
              :errorMessage.sync="itemTypeError"
              :search.sync="itemTypeSearch"
            />
            <InputQuery
              placeholder="Product Origin"
              :items="productOrigin"
              itemKey="Country"
              descKey="Country Code"
              :selectedObject.sync="selectedProductOrigin"
              :isDisabled="viewMode"
              ref="productOrigin"
              :errorMessage.sync="productOriginError"
              :search.sync="productOriginSearch"
            />
            <InputQuery
              placeholder="Metric"
              :items="metric"
              itemKey="UOM Code"
              descKey="Description"
              :selectedObject.sync="selectedMetric"
              :isDisabled="viewMode"
              ref="metric"
              :errorMessage.sync="metricError"
              :search.sync="metricSearch"
            />

            <BoxContainer title="Measurements (per unit)">
              <div class="rowBox">
                <Input
                  placeholder="Weight(Gross KG)"
                  style="padding-right: 5px"
                  :inputValue.sync="gross"
                  :isDisabled="viewMode"
                  ref="gross"
                  validator="positiveDecimal,5"
                />
                <Input
                  placeholder="Weight(Nett KG)"
                  style="padding-right: 5px"
                  :inputValue.sync="nett"
                  :isDisabled="viewMode"
                  ref="weight"
                  validator="positiveDecimal,5"
                />
              </div>

              <div class="rowBox">
                <Input
                  placeholder="Dimension (Length): "
                  style="padding-left: 5px"
                  :inputValue.sync="length"
                  :isDisabled="viewMode"
                  validator="positiveDecimal,5"
                />
                <Input
                  placeholder="Dimension (Width): "
                  style="padding-left: 5px"
                  :inputValue.sync="width"
                  :isDisabled="viewMode"
                  validator="positiveDecimal,5"
                />
                <Input
                  placeholder="Dimension (Height): "
                  style="padding-left: 5px"
                  :inputValue.sync="height"
                  :isDisabled="viewMode"
                  validator="positiveDecimal,5"
                />
                <Input
                  placeholder="Dimension (M3): "
                  style="padding-left: 5px"
                  :inputValue="m3"
                  :isDisabled="true"
                  validator="positiveDecimal,5"
                />
              </div>
            </BoxContainer>
            <br />

            <div class="rowBox">
              <Input
                placeholder="Volume"
                style="padding-right: 5px"
                :inputValue.sync="volume"
                :isDisabled="viewMode"
                ref="volume"
                validator="positiveDecimal,5"
              />
              <Input
                placeholder="GSM"
                style="padding-left: 5px"
                :inputValue.sync="gsm"
                :isDisabled="viewMode"
                ref="GSM"
                validator="positiveDecimal,5"
              />
            </div>
            <div class="rowBox">
              <Input
                placeholder="Grade"
                style="padding-left: 5px"
                :inputValue.sync="grade"
                :isDisabled="viewMode"
                ref="grade"
                validator="text,10"
              />
              <Input
                placeholder="Shade"
                style="padding-left: 5px"
                :errorMessage.sync="shapeError"
                :inputValue.sync="shape"
                :isRequired="true"
                :isDisabled="viewMode"
                ref="shape"
                validator="text,10"
              />
            </div>
            <div class="rowBox">
              <Input
                placeholder="Size"
                style="padding-left: 5px"
                :errorMessage.sync="sizeError"
                :inputValue.sync="size"
                :isRequired="true"
                :isDisabled="viewMode"
                ref="size"
                validator="text,10"
              />
              <Input
                placeholder="Color"
                style="padding-left: 5px"
                :inputValue.sync="color"
                :isDisabled="viewMode"
                ref="color"
                validator="text,50"
              />
            </div>
            <Input
              placeholder="Cust Prod Code"
              :inputValue.sync="cusPcode"
              :isDisabled="viewMode"
              ref="cusPCode"
              validator="text,50"
            />
            <Input
              placeholder="Supp Prod Code"
              :inputValue.sync="supPcode"
              :isDisabled="viewMode"
              ref="supPCode"
              validator="text,50"
            />
          </div>
        </div>

        <div slot="Stock" style="height: 100%">
          <InputQuery
            placeholder="Supplier Code"
            :items="supplierCode"
            itemKey="Supplier Code"
            descKey="Supplier Name"
            :selectedObject.sync="selectedSupCode"
            :isDisabled="viewMode"
            ref="supplierCode"
            :errorMessage.sync="supplierCodeError"
            :search.sync="supCodeSearch"
          />
          <div class="rowBox">
            <InputQuery
              placeholder="Shelf Life"
              :items="duration"
              itemKey="Code"
              descKey="Description"
              :selectedObject.sync="selectedDuration"
              :isDisabled="viewMode"
              ref="duration"
              :errorMessage.sync="durationError"
              :search.sync="durationSearch"
            />
          </div>
          <CheckBoxV2
            title="Stockable"
            :isChecked="stockable"
            :isDisabled="viewMode"
            ref="stockable"
            @checkBoxClicked="stockable = $event"
            style="margin-bottom: 5px"
          />

          <Input
            placeholder="Reorder Level (Bunch)"
            :inputValue.sync="reorder"
            :isDisabled="viewMode"
            ref="reorder"
            validator="decimal,10"
          />
          <Input
            placeholder="Min Stk Level"
            :inputValue.sync="minStock"
            :isDisabled="viewMode"
            ref="minStock"
            validator="decimal,10"
          />
          <Input
            placeholder="Product Class"
            :inputValue.sync="prodClass"
            :isDisabled="viewMode"
            ref="productClass"
            validator="text,10"
          />
          <Input
            placeholder="ABC Class"
            :inputValue.sync="abcClass"
            :isDisabled="viewMode"
            ref="abcClass"
            validator="alphabetic,1,uppercase"
          />
          <Input
            placeholder="ABC Codes"
            :inputValue.sync="abcCode"
            :isDisabled="viewMode"
            ref="abcCode"
            validator="alphabetic,1,uppercase"
          />
          <InputQuery
            placeholder="Barcode Ref"
            :items="barcode"
            itemKey="Barcode Ref - Type"
            descKey="Description"
            :selectedObject.sync="selectedBarcode"
            :isDisabled="viewMode"
            ref="barcode"
            validator="integer,10"
            :errorMessage.sync="barcodeError"
            :search.sync="barcodeSearch"
          />
        </div>

        <div slot="Price" style="height: 100%">
          <div class="rowBox">
            <div class="columnBox" id="priceColumn1">
              <Input
                placeholder="Unit Price (RM)"
                :inputValue.sync="unit"
                :isDisabled="viewMode"
                ref="unitPrice"
                validator="decimal,10"
              />
              <Input
                placeholder="Selling Price (RM)"
                :inputValue.sync="sell"
                :isDisabled="viewMode"
                ref="sellPrice"
                validator="decimal,10"
              />
              <Input
                placeholder="Cost Method"
                :inputValue.sync="cost"
                :isDisabled="viewMode"
                ref="cost"
                validator="text,50"
              />
            </div>
            <div class="columnBox" id="priceColumn2">
              <Input
                placeholder="Price Category"
                :inputValue.sync="price"
                :isDisabled="viewMode"
                ref="priceCategory"
                validator="text,10"
              />
              <Input
                placeholder="Discount %"
                :inputValue.sync="disc"
                :isDisabled="viewMode"
                ref="discount"
                validator="positiveInteger,3"
                :errorMessage.sync="discError"
              />
              <Input
                placeholder="Bill of Material"
                :inputValue.sync="bill"
                :isDisabled="viewMode"
                ref="bill"
                validator="text,50"
              />
            </div>
          </div>
          <div class="rowBox">
            <RemarksBox
              title="Specification"
              :text.sync="specificationValue"
              :isDisabled="viewMode"
              :maxLength="200"
              ref="specification"
            />
          </div>
          <InputQuery
            :isDisabled="viewMode"
            placeholder="GL Acc Code"
            :items="GLAccCode"
            itemKey="Acc No"
            descKey="Description"
            :selectedObject.sync="selectedGLAccCode"
            ref="GLAccCode"
            :errorMessage.sync="gLAccCodeError"
            :search.sync="gLAccCodeSearch"
          />
          <InputQuery
            :isDisabled="viewMode"
            placeholder="GST AR Tax Code"
            :items="GSTARTaxCode"
            itemKey="Tax Code"
            descKey="Tax Code Desc"
            :selectedObject.sync="selectedGSTARTaxCode"
            ref="GSTARTaxCode"
            :errorMessage.sync="gSTARTaxCodeError"
            :search.sync="gSTARTaxCodeSearch"
          />
          <InputQuery
            :isDisabled="viewMode"
            placeholder="GST AR GL Acc No"
            :items="ARGLAccNo"
            itemKey="Acc No"
            descKey="Description"
            :selectedObject.sync="selectedARGLAccNo"
            ref="ARGLAccNo"
            :errorMessage.sync="aRGLAccNoError"
            :search.sync="aRGLAccNoSearch"
          />
          <InputQuery
            :isDisabled="viewMode"
            placeholder="GST AP Tax Code"
            :items="APTaxCode"
            itemKey="Tax Code"
            descKey="Tax Code Desc"
            :selectedObject.sync="selectedAPTaxCode"
            ref="APTaxCode"
            :errorMessage.sync="aPTaxCodeError"
            :search.sync="aPTaxCodeSearch"
          />
          <InputQuery
            :isDisabled="viewMode"
            placeholder="GST AP GL Acc No"
            :items="APGLAccCode"
            itemKey="Acc No"
            descKey="Description"
            :selectedObject.sync="selectedAPGLAccCode"
            ref="APGLAccCode"
            :errorMessage.sync="aPGLAccCodeError"
            :search.sync="aPGLAccCodeSearch"
          />
        </div>

        <div slot="Handling" style="height: 100%">
          <div class="rowBox">
            <RemarksBox
              title="Handling Instruction"
              :text.sync="handlingInstructionValue"
              :isDisabled="viewMode"
              :maxLength="200"
              ref="instruction"
            />
          </div>
          <RadioButtonSet
            title="Stackable"
            :buttons="labelKeys"
            labelKey="label"
            style="display: flex; margin-bottom: 20px"
            :search.sync="selectedStackable"
            :isDisabled="viewMode"
            ref="stackable"
            :selected.sync="selectedStackable"
            :isHorizontal="true"
          />

          <Input
            placeholder="Min Temp (Celsius)"
            :inputValue.sync="minTemp"
            :isDisabled="viewMode"
            ref="minTemp"
            validator="decimal,10,2"
          />
          <Input
            placeholder="Max Temp (Celsius)"
            :inputValue.sync="maxTemp"
            :isDisabled="viewMode"
            ref="maxTemp"
            validator="decimal,10,2"
          />
          <Input
            placeholder="Opt Temp (Celsius)"
            :inputValue.sync="optTemp"
            :isDisabled="viewMode"
            ref="OpTemp"
            validator="decimal,10,2"
          />
          <div class="rowBox">
            <RemarksBox
              title="Remarks"
              :text.sync="remarksValue"
              :isDisabled="viewMode"
              :maxLength="200"
              ref="remarks"
            />
          </div>
        </div>

        <div slot="Customer" style="height: 100%">
          <div class="rowBox" style="height: 100%">
            <div
              class="custDisplay"
              :style="{
                width: showCustDetails ? '60%' : ' 100%',
                height: '100%',
              }"
            >
              <div v-if="!viewMode && !showCustDetails" style="display: flex; justify-content: flex-end">
                <button @click="showCust">Add</button>
                <button @click="deleteCust" style="margin-left: 5px"> Delete </button>
              </div>
              <TableBox
                style="height: calc(100% - 28px)"
                :additionalButtons="viewMode ? [] : custEditButton"
                title="Customer"
                :tableData="customers"
                :selectedRow.sync="selectedCustomers"
                @buttonClicked="editCustDetails"
                :maxSelection="-1"
                :hideColumns="['custaction']"
              />
            </div>
            <div v-if="showCustDetails" style="width: 40%; margin: 5px 10px">
              <div style="display: flex; justify-content: flex-end">
                <button @click="saveCustomer()"> Save </button>
                <button
                  @click="((showCustDetails = false), clearCustomerFields(), (editExistingCustomer = false))"
                  style="margin-left: 5px"
                >
                  Cancel
                </button>
              </div>
              <InputQuery
                placeholder="Customer Code"
                :items="customerCode"
                itemKey="Customer Code"
                descKey="Customer Name"
                :selectedObject.sync="selectedCustomerCode"
                :isDisabled="viewMode"
                ref="customerCode"
                :errorMessage.sync="custCodeError"
                :isRequired="true"
                validator="text,10"
              />
              <Input
                placeholder="Product Code"
                :inputValue.sync="custProdCode"
                :isDisabled="viewMode"
                ref="custProdCode"
                :errorMessage.sync="custProdCodeError"
                :isRequired="true"
                validator="text,10"
              />
              <Input
                placeholder="Product Name"
                :inputValue.sync="custProdName"
                :isDisabled="viewMode"
                ref="custProdName"
                :errorMessage.sync="custProdNameError"
                :isRequired="true"
                validator="text,100"
              />
              <InputQuery
                :items="issueMethod"
                itemKey="Description"
                descKey="ID"
                :selectedObject.sync="selectedIssueMethod"
                placeholder="Issue Method"
                :isDisabled="viewMode"
                ref="issueMethod"
              />
            </div>
          </div>
        </div>

        <div slot="Supplier" style="height: 100%">
          <div style="display: flex; height: 100%">
            <div
              :style="{
                width: showSuppDetails ? '60%' : ' 100%',
                height: '100%',
              }"
            >
              <div v-if="!viewMode && !showSuppDetails" style="display: flex; justify-content: flex-end">
                <button @click="showSupp">Add</button>
                <button @click="deleteSuppDetails" style="margin-left: 5px"> Delete </button>
              </div>

              <TableBox
                style="height: calc(100% - 28px)"
                :additionalButtons="viewMode ? [] : suppEditButton"
                title="Supplier"
                :tableData="suppliers"
                :selectedRow.sync="selectedSupplier"
                @buttonClicked="editSuppDetails"
                :maxSelection="-1"
                :hideColumns="['suppaction']"
              />
            </div>
            <div v-if="showSuppDetails" style="width: 40%; margin: 5px 10px; height: 100%">
              <div style="display: flex; justify-content: flex-end">
                <button @click="saveSupplier">Save</button>
                <button
                  @click="((showSuppDetails = false), clearSupplierTeamsField(), (editExistingSupplier = false))"
                  style="margin-left: 5px"
                >
                  Cancel
                </button>
              </div>
              <InputQuery
                placeholder="Supplier Code"
                :items="supplierData"
                itemKey="Supplier Code"
                descKey="Supplier Name"
                :selectedObject.sync="selectedSupplierData"
                :isDisabled="viewMode"
                ref="supplierCode"
                :errorMessage.sync="suppCodeError"
                :isRequired="true"
              />
              <Input
                placeholder="Product Code"
                :inputValue.sync="supProdCode"
                :isDisabled="viewMode"
                ref="supplierProductCode"
                :errorMessage.sync="supProdCodeError"
                :isRequired="true"
                validator="text,10"
              />
              <Input
                placeholder="Product Name"
                :inputValue.sync="supProdName"
                :isDisabled="viewMode"
                ref="supplierProductName"
                :errorMessage.sync="supProdNameError"
                :isRequired="true"
                validator="text,100"
              />
            </div>
          </div>
        </div>

        <div slot="Packaging" style="height: 100%">
          <InputQuery
            placeholder="Package Code/UOM (BUNCH)"
            :items="subPkg"
            itemKey="Package Code"
            :isRequired="true"
            descKey="Description"
            :selectedObject.sync="selectedBunch"
            :isDisabled="viewMode"
            :errorMessage.sync="bunchError"
            ref="bunch"
            :search.sync="bunchSearch"
          />

          <RadioButtonSet
            title="Loose Package"
            :buttons="labelKeys"
            labelKey="label"
            style="display: flex; margin-bottom: 20px"
            :search.sync="selectedLoose"
            :isDisabled="viewMode"
            ref="loose"
            :selected.sync="selectedLoose"
            :isHorizontal="true"
          />

          <CheckBoxV2
            title="Sub Package"
            :isDisabled="viewMode"
            @checkBoxClicked="selectedSub = $event"
            :isChecked="selectedSub"
          />

          <div style="display: grid; grid-template-columns: 1fr 5fr; margin-bottom: 10px">
            <img
              src="https://dsmlib.logistics.systems/Icons/subpackageScale.png"
              alt="scale"
              style="height: 150px; justify-self: end"
            />

            <div style="display: grid; grid-template-columns: 1fr 0.5fr 0.5fr 0.5fr 2fr; align-items: center">
              <p style="text-align: center">Largest Sized Sub Package</p>
              <img
                src="https://dsmlib.logistics.systems/Icons/subpackage1.png"
                alt="Sub Package 1"
                style="height: 50px; width: 50px; justify-self: center"
              />

              <Input
                placeholder="Quantity"
                :inputValue.sync="subpkg1qty"
                :isDisabled="viewMode || subpackageRow1"
                ref="subPkgQuantity1"
                validator="integer,5"
                :errorMessage.sync="subPkg1QtyError"
              />
              <div style="font-size: 30px; margin-top: -5px; text-align: center"> &times; </div>
              <InputQuery
                placeholder="Sub Package"
                :items="subPkg"
                itemKey="Package Code"
                descKey="Description"
                :selectedObject.sync="selectedSubPkg1"
                :isDisabled="viewMode || subpackageRow1"
                ref="subpkg1"
                :errorMessage.sync="subPkg1Error"
                :search.sync="subPkg1Search"
              />

              <p style="text-align: center">Middle Sized Sub Package</p>
              <img
                src="https://dsmlib.logistics.systems/Icons/subpackage2.png"
                alt="Sub Package 2"
                style="height: 50px; width: 50px; justify-self: center"
              />

              <Input
                placeholder="Quantity"
                :inputValue.sync="subpkg2qty"
                :isDisabled="viewMode || subpackageRow2"
                ref="subPkgQuantity2"
                validator="integer,5"
                :errorMessage.sync="subPkg2QtyError"
              />

              <div style="font-size: 30px; margin-top: -5px; text-align: center"> &times; </div>
              <InputQuery
                placeholder="Sub Package"
                :items="subPkg"
                itemKey="Package Code"
                descKey="Description"
                :selectedObject.sync="selectedSubPkg2"
                :isDisabled="viewMode || subpackageRow2"
                ref="subpkg2"
                :errorMessage.sync="subPkg2Error"
                :search.sync="subPkg2Search"
              />

              <p style="text-align: center; vertical-align: center"> Smallest Sized Sub Package </p>

              <img
                src="https://dsmlib.logistics.systems/Icons/subpackage3.png"
                alt="Sub Package 3"
                style="height: 30px; width: 30px; justify-self: center"
              />

              <Input
                placeholder="Quantity"
                :inputValue.sync="subpkg3qty"
                :isDisabled="viewMode || subpackageRow3"
                ref="subPkgQuantity3"
                validator="integer,5"
                :errorMessage.sync="subPkg3QtyError"
              />
              <div style="font-size: 30px; margin-top: -5px; text-align: center"> &times; </div>
              <InputQuery
                placeholder="Sub Package"
                :items="subPkg"
                itemKey="Package Code"
                descKey="Description"
                :selectedObject.sync="selectedSubPkg3"
                :isDisabled="viewMode || subpackageRow3"
                ref="subpkg3"
                :errorMessage.sync="subPkg3Error"
                :search.sync="subPkg3Search"
              />
            </div>
          </div>

          <Input
            placeholder="Qty per pallet"
            :inputValue.sync="pallet"
            :isDisabled="viewMode"
            ref="pallet"
            validator="integer,5"
          />
        </div>

        <div slot="Attachments" style="height: 100%">
          <Attachments
            :attachments.sync="attachments"
            :viewMode="viewMode"
            style="height: 100%"
            @attachmentsAdded="addAttachment"
            @attachmentsDeleted="deleteAttachment"
            remarksLength="50"
          />
        </div>

        <div slot="Composition" style="height: 100%">
          <div style="display: flex; height: 100%">
            <div
              :style="{
                width: showCompDetails ? '60%' : ' 100%',
                height: '100%',
              }"
            >
              <div v-if="!viewMode && !showCompDetails" style="display: flex; justify-content: flex-end">
                <button @click="showCompDetails = true">Add</button>
                <button @click="deleteComposition" style="margin-left: 5px"> Delete </button>
              </div>

              <TableBox
                style="height: calc(100% - 28px)"
                :additionalButtons="viewMode ? [] : compEditButton"
                title="Composition"
                :tableData="compositions"
                :selectedRow.sync="selectedCompositions"
                :maxSelection="-1"
                @buttonClicked="editComposition"
                :hideColumns="['subaction']"
              />
            </div>
            <div v-if="showCompDetails" style="width: 40%; margin: 5px 10px">
              <div style="display: flex; justify-content: flex-end">
                <button @click="saveComposition">Save</button>
                <button @click="((showCompDetails = false), clearCompositionFields())" style="margin-left: 5px">
                  Cancel
                </button>
              </div>
              <InputQuery
                placeholder="Sub Product ID"
                :items="subProdIDData"
                itemKey="Product Code"
                descKey="Description"
                :selectedObject.sync="selectedSubProdID"
                :isDisabled="viewMode"
                ref="subProdID"
                :errorMessage.sync="subProdIdError"
                :isRequired="true"
              />
              <Input
                placeholder="Sub Product Code"
                :inputValue.sync="subCode"
                :isDisabled="viewMode"
                ref="subCode"
                :errorMessage.sync="subCodeError"
                validator="text,50"
                :isRequired="true"
              />
              <Input
                placeholder="Sub Quantity"
                :inputValue.sync="subQty"
                :isDisabled="viewMode"
                ref="subQty"
                :errorMessage.sync="subQtyError"
                :isRequired="true"
                validator="decimal,10"
              />
            </div>
          </div>
        </div>
      </Tab>
    </PopupEntry>
  </div>
</template>
<script>
/* eslint-disable */
import Attachments from '@/components/tabComponents/Attachments.vue';

export default {
  components: {
    Attachments,
  },
  computed: {
    m3: function () {
      if (this.length !== '' && this.width !== '' && this.height !== '') {
        return (+this.length * +this.width * +this.height).toString();
      } else {
        return '';
      }
    },
  },
  data: function () {
    return {
      menus: [
        {
          title: 'New Warehouse Product',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-add-new-50.png',
          visible: true,
          enabled: true,
          key: 'MnuNew',
        },
        {
          title: 'Edit Warehouse Product',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-edit-64%20(1).png',
          visible: true,
          enabled: true,
          key: 'MnuEdit',
        },
        {
          title: 'Delete Warehouse Product',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-reduce-50.png',
          visible: false,
          enabled: true,
          key: 'MnuDelete',
        },
        {
          title: 'View Warehouse Product',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-view-64.png',
          visible: true,
          enabled: true,
          key: 'MnuQuery',
        },
      ],

      prodID: '******',
      prodIDError: '',
      prodCode: '',
      prodCodeError: '',
      prodName: '',
      prodNameError: '',
      disabledProdID: false,
      disabledProdCode: false,
      disabledProdName: false,
      deletedAttachments: [],
      tabIndex: 0,

      //general details
      selectedItemType: null,
      itemTypeSearch: '',
      itemTypeError: '',
      selectedProductOrigin: null,
      productOriginSearch: '',
      productOriginError: '',
      selectedMetric: null,
      metricSearch: '',
      metricError: '',
      gross: 0,
      nett: 0,
      length: 0,
      width: 0,
      height: 0,
      volume: 0,
      gsm: 0,
      grade: '',
      shape: '',
      shapeError: '',
      size: '',
      sizeError: '',
      color: '',
      cusPcode: '',
      supPcode: '',

      //stock
      selectedSupCode: null,
      supCodeSearch: '',
      supCodeError: '',
      selectedDuration: null,
      durationSearch: '',
      supplierCodeError: '',
      durationError: '',
      stockable: false,
      reorder: 0,
      minStock: 0,
      prodClass: '',
      abcClass: '',
      abcCode: '',
      selectedBarcode: null,
      barcodeSearch: '',
      barcodeError: '',

      //price
      unit: 0,
      price: '',
      sell: 0,
      disc: 0,
      discError: '',
      cost: '',
      bill: '',
      specificationValue: '',
      selectedGLAccCode: null,
      gLAccCodeSearch: '',
      gLAccCodeError: '',
      selectedGSTARTaxCode: null,
      gSTARTaxCodeSearch: '',
      gSTARTaxCodeError: '',
      selectedARGLAccNo: null,
      aRGLAccNoSearch: '',
      aRGLAccNoError: '',
      ARGLAccNo: null,
      aRGLAccNoSearch: '',
      aRGLAccNoError: '',
      selectedAPTaxCode: null,
      aPTaxCodeSearch: '',
      aPTaxCodeError: '',
      selectedAPGLAccCode: null,
      aPGLAccCodeSearch: '',
      aPGLAccCodeError: '',

      //handling
      handlingInstructionValue: '',
      selectedStackable: null,
      labelKeys: [{ label: 'Yes' }, { label: 'No' }],
      minTemp: 0,
      maxTemp: 0,
      optTemp: 0,
      remarksValue: '',

      //ownership
      selectedCustomerCode: null,
      custCodeError: '',
      custProdCode: '',
      custProdCodeError: '',
      custProdName: '',
      custProdNameError: '',
      selectedIssueMethod: null,
      selectedSupplierData: null,
      suppCodeError: '',
      supProdCode: '',
      supProdCodeError: '',
      supProdName: '',
      supProdNameError: '',

      //packaging
      selectedBunch: null,
      bunchSearch: '',
      bunchError: '',
      // radio button
      selectedLoose: null,
      // sub packages checkbox
      selectedSub: false,
      // subpackages
      selectedSubPkg1: null,
      subPkg1Search: '',
      subPkg1Error: '',
      subPkg1QtyError: '',
      subpkg1qty: 0,
      selectedSubPkg2: null,
      subPkg2Search: '',
      subPkg2Error: '',
      subPkg2QtyError: '',
      subpkg2qty: 0,
      selectedSubPkg3: null,
      subPkg3Search: '',
      subPkg3Error: '',
      subPkg3QtyError: '',
      subpkg3qty: 0,
      pallet: 0,

      //attachments
      attachments: [],
      existingAttachments: [],

      customers: [],
      deletedCustomers: [],

      suppliers: [],
      deletedSuppliers: [],

      compositions: [],
      deletedCompositions: [],

      //composition
      selectedSubProdID: [],
      subProdIdError: '',
      subQty: '',
      subQtyError: '',
      subCode: '',
      subCodeError: '',

      warehouseData: [],
      selectedWarehouseProduct: [],

      showCustDetails: false,
      custEditButton: [{ text: 'Edit', index: 0, width: 60 }],
      CustomerData: [],
      selectedCustomers: [],

      showSuppDetails: false,
      suppEditButton: [{ text: 'Edit', index: 0, width: 60 }],
      supplierDetails: [],
      selectedSupplier: [],

      showCompDetails: false,
      compEditButton: [{ text: 'Edit', index: 0, width: 60 }],

      itemType: [],
      selectedItemType: null,

      productOrigin: [],
      selectedProductOrigin: null,

      metric: [],
      selectedMetric: null,

      supplierCode: [],
      selectedSupcode: null,

      duration: [],
      selectedDuration: null,

      barcode: [],
      selectedBarcode: null,

      GLAccCode: [],
      selectedGLAccCode: null,

      GSTARTaxCode: [],
      selectedGSTARTaxCode: null,

      APTaxCode: [],
      selectedAPTaxCode: null,

      APGLAccCode: [],
      selectedAPGLAccCode: null,

      stackable: [],
      selectedStackable: null,

      customerCode: [],
      selectedCustomerCode: null,

      issueMethod: [],
      selectedIssueMethod: null,

      supplierData: [],
      selectedSupplierData: [],

      loose: [],

      sub: [],

      compositionData: [],
      selectedCompositions: [],

      subProdIDData: [],
      selectedSubProdID: null,

      // Popup entry data
      popupEntryShow: false,
      popupEntryTitle: '',
      createdBy: '',
      updatedBy: '',
      lastUpdate: '',
      viewMode: false,
      entryRequired: [],
      entrySuccess: [],
      entryMessages: [],
      entryError: [],

      warehouseDetails: {},

      currentTab: 0,

      tabsItems: [
        { title: 'General Details', disabled: false },
        { title: 'Stock', disabled: false },
        { title: 'Price', disabled: false },
        { title: 'Handling', disabled: false },
        { title: 'Customer', disabled: false },
        { title: 'Supplier', disabled: false },
        { title: 'Packaging', disabled: false },
        { title: 'Attachments', disabled: false },
        { title: 'Composition', disabled: false },
      ],

      errorMapping: [
        { key: 'productID', tab: 0 },
        { key: 'productCode', tab: 0 },
        { key: 'productName', tab: 0 },
        { key: 'shape', tab: 0 },
        { key: 'size', tab: 0 },
        { key: 'bunch', tab: 6 },
        { key: 'itemType', tab: 0 },
        { key: 'productOrigin', tab: 0 },
        { key: 'metric', tab: 0 },
        { key: 'supplierCode', tab: 1 },
        { key: 'duration', tab: 1 },
        { key: 'barcode', tab: 1 },
        { key: 'GLAccCode', tab: 2 },
        { key: 'GSTARTaxCode', tab: 2 },
        { key: 'ARGLAccNo', tab: 2 },
        { key: 'APTaxCode', tab: 2 },
        { key: 'APGLAccCode', tab: 2 },
        { key: 'subpkg1', tab: 6 },
        { key: 'subpkg2', tab: 6 },
        { key: 'subpkg3', tab: 6 },
      ],

      // Attachment tab data

      // search input fields for query selector
      durationSearch: '',
      barcodeSearch: '',
      gLAccCodeSearch: '',
      gSTARTaxCodeSearch: '',
      aRGLAccNoSearch: '',
      aPTaxCodeSearch: '',
      aPGLAccCodeSearch: '',
      bunchSearch: '',
      subPkg1Search: '',
      subPkg2Search: '',
      subPkg3Search: '',

      editExistingCustomer: false,
      editNewCustomer: false,
      customerIndex: null,

      editExistingSupplier: false,
      editNewSupplier: false,
      supplierIndex: null,

      editExistingComposition: false,
      editNewComposition: false,
      compositionIndex: null,

      // subpackage input enable / disable
      subpackageRow1: true,
      subpackageRow2: true,
      subpackageRow3: true,
      subPkg: [],
    };
  },
  props: {},
  methods: {
    tabClicked: async function (tabItem, tabIndex) {
      if (this.showCustDetails && tabIndex !== 4) {
        const discardChanges = await this.promptDiscardWHProductChanges();
        if (discardChanges === 'Cancel') {
          return;
        }
        this.showCustDetails = false;
        this.clearCustomerFields();
        this.showCustDetails = false;
        this.customerIndex = null;
        this.editExistingCustomer = false;
        this.editNewCustomer = false;
      }

      if (this.showSuppDetails && tabIndex !== 5) {
        const discardChanges = await this.promptDiscardWHProductChanges();
        if (discardChanges === 'Cancel') {
          return;
        }
        this.clearSupplierTeamsField();
        this.showSuppDetails = false;
        this.supplierIndex = null;
        this.editExistingSupplier = false;
        this.editNewSupplier = false;
        this.selectedSupplierData = null;
      }

      if (this.showCompDetails && tabIndex !== 8) {
        const discardChanges = await this.promptDiscardWHProductChanges();
        if (discardChanges === 'Cancel') {
          return;
        }
        this.clearCompositionFields();
        this.showCompDetails = false;
        this.compositionIndex = null;
        this.editExistingComposition = false;
        this.editNewComposition = false;
      }

      this.currentTab = tabIndex;
    },
    handleRepeatedProductCode: async function (state) {
      if (!state) {
        const productCodes = this.warehouseData.map((item) => item['Product Code']);
        if (productCodes.includes(this.prodCode.toUpperCase().trim()) && this.warehouseAction === 'New') {
          const editProductCode = await this.$showAlert(
            'warning',
            'Product Code already exist',
            "Please go to 'Edit Warehouse Product' to make changes",
            [{ text: 'Edit' }, { text: 'Cancel', color: 'lightgrey' }],
          );
          if (editProductCode === 'Edit') {
            // find the product ID of the product code
            const warehouseProduct = this.warehouseData.filter(
              (item) => item['Product Code'] === this.prodCode.toUpperCase().trim(),
            );
            let product = await this.getProductDetails(warehouseProduct[0]['Product ID']);
            this.populatePopupEntryFields(product);
            this.warehouseAction = 'Edit';
            this.popupEntryTitle = `Edit Warehouse Product`;
          }
          this.$refs['productCode'].setFocus();
        }
      }
    },
    promptDiscardWHProductChanges: function () {
      // $showAlert returns a promise, which can be awaited in the calling function to get the button clicked by the user
      return this.$showAlert(
        'warning',
        'Discard Changes',
        'You have unsaved warehouse product changes.<br>Do you wish to discard the changes?',
        [{ text: 'Discard', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
      );
    },
    saveCustomer: function () {
      if (this.checkCust()) {
        return;
      }

      let repeated = this.customers.findIndex(
        (c, cIndex) =>
          cIndex !== this.customerIndex &&
          c['Cust Code'] === this.selectedCustomerCode['Customer Code'] &&
          c['Cust Prod Code 1'] === this.custProdCode &&
          c['Cust Prod Name'] === this.custProdName &&
          (this.selectedIssueMethod != null ?
            c['Issue Method'].toString() === this.selectedIssueMethod['ID'].toString()
          : false),
      );

      if (repeated !== -1) {
        this.$showAlert('error', 'Error Saving Customer', 'This customer product already exist.');
        return;
      }

      let customer = {
        custaction: this.editExistingCustomer === true ? 'E' : 'N',
        ID: '',
        TxnID: this.editExistingCustomer ? parseInt(this.customers[this.customerIndex]['TxnID']) : '',
        'Cust Code': this.selectedCustomerCode['Customer Code'],
        'Cust Prod Code 1': this.custProdCode,
        'Cust Prod Name': this.custProdName,
        'Issue Method': this.selectedIssueMethod ? this.selectedIssueMethod['ID'] : '',
        'Issue Method Desc': this.selectedIssueMethod ? this.selectedIssueMethod['Description'] : '',
      };

      if (this.editExistingCustomer || this.editNewCustomer) {
        this.customers[this.customerIndex] = {
          ...this.customers[this.customerIndex],
          ...customer,
        };
      } else {
        this.customers.push(customer);
      }

      this.refreshCustomers(this.customers);

      this.clearCustomerFields();
      this.showCustDetails = false;
      this.customerIndex = null;
      this.editExistingCustomer = false;
      this.editNewCustomer = false;
    },

    deleteCust: function () {
      if (this.selectedCustomers.length == 0) {
        this.$showAlert('error', 'No Customer selected', 'Please select at least 1 Customer to delete');
        return;
      }

      this.selectedCustomers.forEach((selectedCustomer) => {
        const customerIndex = this.customers.findIndex(
          (customer) =>
            customer['Cust Code'] === selectedCustomer['Cust Code'] &&
            customer['Cust Prod Code 1'] === selectedCustomer['Cust Prod Code 1'] &&
            customer['Cust Prod Name'] === selectedCustomer['Cust Prod Name'],
        );
        if (customerIndex !== -1) {
          if (this.customers[customerIndex]['#'] !== undefined) {
            this.customers[customerIndex]['custaction'] = 'D';
            this.deletedCustomers.push(this.customers[customerIndex]);
          }
          this.customers.splice(customerIndex, 1);
        }
      });

      this.refreshCustomers(this.customers);
    },
    refreshCustomers: function (customers) {
      this.customers = [];
      this.$nextTick(() => {
        this.customers = customers;
        this.selectedCustomers = [];
      });
    },
    refreshSuppliers: function (suppliers) {
      this.suppliers = [];
      this.$nextTick(() => {
        this.suppliers = suppliers;
        this.selectedSupplier = [];
      });
    },
    refreshCompositions: function (compositions) {
      this.compositions = [];
      this.$nextTick(() => {
        this.compositions = compositions;
        this.selectedCompositions = [];
      });
    },

    editCustDetails: function (btn, data) {
      if (data['#'] !== undefined) {
        this.editExistingCustomer = true;
      } else {
        this.editNewCustomer = true;
      }
      if (data['Issue Method'] === null) {
        this.selectedIssueMethod = this.issueMethod.getSelected('4', 'ID');
      } else {
        this.selectedIssueMethod = this.issueMethod.getSelected(data['Issue Method'], 'ID');
      }

      this.showCustDetails = true;
      this.selectedCustomerCode = this.customerCode.getSelected(data['Cust Code'], 'Customer Code');
      this.custProdCode = data['Cust Prod Code 1'];
      this.custProdName = data['Cust Prod Name'];

      this.customerIndex = this.customers.findIndex(
        (c) =>
          c['Cust Code'] === data['Cust Code'] &&
          c['Cust Prod Code 1'] === data['Cust Prod Code 1'] &&
          c['Cust Prod Name'] === data['Cust Prod Name'],
      );
    },

    clearCustomerFields: function () {
      this.selectedCustomerCode = null;
      this.custProdCode = '';
      this.custProdName = '';
      this.selectedIssueMethod = null;
      this.custCodeError = '';
      this.custProdCodeError = '';
      this.custProdNameError = '';
    },

    showCust: function () {
      this.showCustDetails = true;
      this.selectedIssueMethod = this.issueMethod.getSelected('4', 'ID');
    },

    saveSupplier: function () {
      let notValid = this.checkSupp();
      if (notValid) {
        return;
      }

      let repeated = this.suppliers.findIndex(
        (s, sIndex) =>
          sIndex !== this.supplierIndex &&
          s['Supp Code'] === this.selectedSupplierData['Supplier Code'] &&
          s['Supp Prod Code 1'] === this.supProdCode &&
          s['Supp Prod Name'] === this.supProdName,
      );

      if (repeated !== -1) {
        this.$showAlert('error', 'Error Saving Supplier', 'This supplier product already exist.');
        return;
      }

      let supplier = {
        suppaction: this.editExistingSupplier === true ? 'E' : 'N',
        ID: '',
        TxnID: this.editExistingSupplier ? parseInt(this.suppliers[this.supplierIndex]['TxnID']) : '',
        'Supp Code': this.selectedSupplierData['Supplier Code'],
        'Supp Prod Code 1': this.supProdCode,
        'Supp Prod Name': this.supProdName,
      };

      if (this.editExistingSupplier || this.editNewSupplier) {
        this.suppliers[this.supplierIndex] = {
          ...this.suppliers[this.supplierIndex],
          ...supplier,
        };
      } else {
        this.suppliers.push(supplier);
      }

      this.refreshSuppliers(this.suppliers);

      this.clearSupplierTeamsField();
      this.showSuppDetails = false;
      this.supplierIndex = null;
      this.editExistingSupplier = false;
      this.editNewSupplier = false;
      this.selectedSupplierData = null;
    },

    deleteSuppDetails: function () {
      if (this.selectedSupplier.length == 0) {
        this.$showAlert('error', 'No Supplier selected', 'Please select at least 1 Supplier to delete');
        return;
      }

      this.selectedSupplier.forEach((selectedSupplier) => {
        const supplierIndex = this.suppliers.findIndex(
          (supplier) =>
            supplier['Supp Code'] === selectedSupplier['Supp Code'] &&
            supplier['Supp Prod Code 1'] === selectedSupplier['Supp Prod Code 1'] &&
            supplier['Supp Prod Name'] === selectedSupplier['Supp Prod Name'],
        );
        if (supplierIndex !== -1) {
          if (this.suppliers[supplierIndex]['#'] !== undefined) {
            this.suppliers[supplierIndex]['suppaction'] = 'D';
            this.deletedSuppliers.push(this.suppliers[supplierIndex]);
          }
          this.suppliers.splice(supplierIndex, 1);
        }
      });
      this.refreshSuppliers(this.suppliers);
    },

    editSuppDetails: function (btn, data) {
      if (data['#'] !== undefined) {
        this.editExistingSupplier = true;
      } else {
        this.editNewSupplier = true;
      }
      this.showSuppDetails = true;
      this.selectedSupplierData = this.supplierData.getSelected(data['Supp Code'], 'Supplier Code');
      this.supProdCode = data['Supp Prod Code 1'];
      this.supProdName = data['Supp Prod Name'];

      this.supplierIndex = this.suppliers.findIndex(
        (s) =>
          s['Supp Code'] === data['Supp Code'] &&
          s['Supp Prod Code 1'] === data['Supp Prod Code 1'] &&
          s['Supp Prod Name'] === data['Supp Prod Name'],
      );
    },

    clearSupplierTeamsField: function () {
      this.selectedSupplierData = null;
      this.supProdCode = '';
      this.supProdName = '';
      this.suppCodeError = '';
      this.supProdCodeError = '';
      this.supProdNameError = '';
    },

    showSupp: function () {
      this.showSuppDetails = true;
    },

    saveComposition: function () {
      if (this.checkComp()) {
        return;
      }

      let repeated = this.compositions.findIndex(
        (c) =>
          c['Sub Product Id'] === this.selectedSubProdID['Product ID'] &&
          c['Sub Product Code'] === this.subCode &&
          c['Qty'] === this.subQty,
      );

      if (repeated !== -1) {
        this.$showAlert('error', 'Error Saving Composition', 'This composition already exist.');
        return;
      }

      let composition = {
        subaction: this.editExistingComposition === true ? 'E' : 'N',
        ID: '',
        TxnID: this.editExistingComposition ? parseInt(this.composition[this.compositionIndex]['TxnID']) : '',
        PProdCode: this.selectedSubProdID['Product Code'],
        'Sub Product Id': this.selectedSubProdID['Product ID'],
        'Sub Product Code': this.subCode,
        Qty: this.subQty,
      };

      if (this.editExistingComposition || this.editNewComposition) {
        this.compositions[this.compositionIndex] = {
          ...this.compositions[this.compositionIndex],
          ...composition,
        };
      } else {
        this.compositions.push(composition);
      }

      this.refreshCompositions(this.compositions);

      this.clearCompositionFields();
      this.showCompDetails = false;
      this.compositionIndex = null;
      this.editExistingComposition = false;
      this.editNewComposition = false;
    },

    deleteComposition: function () {
      if (this.selectedCompositions.length == 0) {
        this.$showAlert('error', 'No Composition selected', 'Please select at least 1 Composition to delete');
        return;
      }
      this.selectedCompositions.forEach((selectedComposition) => {
        const compositionIndex = this.compositions.findIndex(
          (composition) =>
            composition['Sub Product Id'] === selectedComposition['Sub Product Id'] &&
            composition['Sub Product Code'] === selectedComposition['Sub Product Code'],
        );
        if (compositionIndex !== -1) {
          if (this.compositions[compositionIndex]['SeqNo'] !== undefined) {
            this.compositions[compositionIndex]['subaction'] = 'D';
            this.deletedCompositions.push(this.compositions[compositionIndex]);
          }
          this.compositions.splice(compositionIndex, 1);
        }
      });

      this.refreshCompositions(this.compositions);
    },

    editComposition: function (btn, data) {
      if (data['SeqNo'] !== undefined) {
        this.editExistingComposition = true;
      } else {
        this.editNewComposition = true;
      }
      this.showCompDetails = true;
      this.selectedSubProdID = this.subProdIDData.getSelected(data['PProdCode'], 'Product Code');
      this.subQty = data['Qty'];
      this.subCode = data['Sub Product Code'];
      this.compositionIndex = this.compositions.findIndex(
        (c) =>
          c['Sub Product Id'] === data['Sub Product Id'] &&
          c['Sub Product Code'] === data['Sub Product Code'] &&
          c['Qty'] === data['Qty'],
      );
    },

    clearCompositionFields: function () {
      this.selectedSubProdID = null;
      this.subQty = '';
      this.subCode = '';
      this.subProdIdError = '';
      this.subCodeError = '';
      this.subQtyError = '';
    },

    getProductDetails: async function (wareProdDetails) {
      this.$fullscreenLoading('Getting Details');
      let product = await this.$apiCallV2('/Master/GetDetailsMaster', {
        recordid: wareProdDetails,
        masterfile: 'WHPRODUCT',
      });
      this.$fullscreenLoading();
      return product;
    },

    checkCust: function () {
      let error = false;

      if (!this.custProdCode || this.custProdCode.trim().length === 0) {
        this.custProdCodeError = 'Customer Product Code is required!';
        error = true;
      } else if (this.custProdCode.length > 50) {
        this.custProdCodeError = 'Customer Product Code cannot exceed 50 characters!';
        error = true;
      }
      if (!this.custProdName || this.custProdName.trim().length === 0) {
        this.custProdNameError = 'Customer Product Name is required!';
        error = true;
      } else if (this.custProdName.length > 50) {
        this.custProdNameError = 'Customer Product Name cannot exceed 50 characters!';
        error = true;
      }
      if (!this.selectedCustomerCode) {
        this.custCodeError = 'Customer Code is required!';
        error = true;
      }
      return error;
    },

    checkSupp: function () {
      let error = false;

      if (!this.supProdCode || this.supProdCode.trim().length === 0) {
        this.supProdCodeError = 'Product Code is required!';
        error = true;
      } else if (this.supProdCode.length > 50) {
        this.supProdCodeError = 'Product Code cannot exceed 50 characters!';
        error = true;
      }
      if (!this.supProdName || this.supProdName.trim().length === 0) {
        this.supProdNameError = 'Product Name is required!';
        error = true;
      } else if (this.supProdName.length > 50) {
        this.supProdNameError = 'Product Name cannot exceed 50 characters!';
        error = true;
      }
      if (!this.selectedSupplierData) {
        this.suppCodeError = 'Supplier Code is required!';
        error = true;
      }
      return error;
    },

    checkComp: function () {
      let error = false;

      if (!this.subQty) {
        this.subQtyError = 'Quantity is required!';
        error = true;
      } else if (this.subQty.length > 100) {
        // Check if Customer Product Code exceeds 50 characters
        this.subQtyError = 'Product Name cannot exceed 100 characters!';
        error = true;
      }
      if (!this.selectedSubProdID) {
        this.subProdIdError = 'Sub Product Id is required!';
        error = true;
      }
      if (!this.subCode || this.subCode.trim().length === 0) {
        this.subCodeError = 'Sub Product Code is required!';
        error = true;
      }
      return error;
    },

    saveClicked: async function () {
      if (this.showCustDetails || this.showSuppDetails || this.showCompDetails) {
        const discardChanges = await this.promptDiscardWHProductChanges();
        if (discardChanges === 'Cancel') {
          return;
        }
      }
      let customersToSend = [];
      customersToSend.push(...this.customers);
      customersToSend.push(...this.deletedCustomers);

      let suppliersToSend = [];
      suppliersToSend.push(...this.suppliers);
      suppliersToSend.push(...this.deletedSuppliers);

      let compositionsToSend = [];
      compositionsToSend.push(...this.compositions);
      compositionsToSend.push(...this.deletedCompositions);

      this.clearErrors();

      if (!this.checkFields()) {
        let dataToPost = {
          action: this.warehouseAction === 'New' ? 'N' : 'E',
          id: this.warehouseAction !== 'New' ? this.prodID : '',
          productcode: this.prodCode.toUpperCase().trim(),
          productname: this.prodName.toUpperCase().trim(),
          itemtype: this.selectedItemType !== null ? this.selectedItemType['Item Type'] : '',

          origin: this.selectedProductOrigin !== null ? this.selectedProductOrigin['Country Code'] : '',
          metric: this.selectedMetric !== null ? this.selectedMetric['UOM Code'] : '',
          grossweight: this.gross !== '' ? parseFloat(this.gross) : 0,
          nettweight: this.nett !== '' ? parseFloat(this.nett) : 0,
          length: this.length !== '' ? parseFloat(this.length) : 0,
          width: this.width !== '' ? parseFloat(this.width) : 0,
          height: this.height !== '' ? parseFloat(this.height) : 0,
          m3: this.m3 !== '' ? parseFloat(this.m3) : 0,
          volume: this.volume !== '' ? parseFloat(this.volume) : 0,
          gsm: this.gsm !== '' ? parseFloat(this.gsm) : 0,
          grade: this.grade,
          shape: this.shape,
          size: this.size,
          colour: this.color,
          custprodcode: this.cusPcode,
          suppprodcode: this.supPcode,
          suppliercode: this.selectedSupCode !== null ? this.selectedSupCode['Supplier Code'] : '',
          shelflifeduration: this.selectedDuration !== null ? this.selectedDuration['Code'] : '',
          shelflife: 0,
          stockable: this.stockable === true ? '1' : '0',
          reoderlevel: this.reorder !== '' ? parseFloat(this.reorder) : 0,
          minstocklevel: this.minStock !== '' ? parseInt(this.minStock) : 0,
          productclass: this.prodClass,
          abcclass: this.abcClass,
          abccode: this.abcCode,
          barcoderef: this.selectedBarcode !== null ? this.selectedBarcode['BarCode Reference'] : 0,
          unitprice: this.unit !== '' ? parseFloat(this.unit) : 0,
          sellingprice: this.sell !== '' ? parseFloat(this.sell) : 0,
          discount: this.disc !== '' ? parseInt(this.disc) : 0,
          pricecategory: this.price,
          costmethod: this.cost,
          billofmaterial: this.bill,
          specification: this.specificationValue,
          glcode: this.selectedGLAccCode !== null ? this.selectedGLAccCode['Acc No'] : '',
          gstartaxcode: this.selectedGSTARTaxCode !== null ? this.selectedGSTARTaxCode['Tax Code'] : '',
          gstarglcode: this.selectedARGLAccNo !== null ? this.selectedARGLAccNo['Acc No'] : '',
          gstaptaxcode: this.selectedAPTaxCode !== null ? this.selectedAPTaxCode['Tax Code'] : '',
          gstapglcode: this.selectedAPGLAccCode !== null ? this.selectedAPGLAccCode['Acc No'] : '',
          handlinginstruction: this.handlingInstructionValue,

          stackable:
            this.selectedStackable === null ? '0'
            : this.selectedStackable['label'] === 'Yes' ? '1'
            : '0',
          mintemp: this.minTemp !== '' ? parseFloat(this.minTemp) : 0,
          maxtemp: this.maxTemp !== '' ? parseFloat(this.maxTemp) : 0,
          opttemp: this.optTemp !== '' ? parseFloat(this.optTemp) : 0,
          remarks: this.remarksValue,
          packagecode: this.selectedBunch['Package Code'], // Required field

          loosepacking:
            this.selectedLoose === null ? '0'
            : this.selectedLoose['label'] === 'Yes' ? '1'
            : '0',
          subpackage: this.selectedSub === true ? '1' : '0',
          subpkg1qty: this.subpkg1qty !== 0 ? this.subpkg1qty : 0,
          subpkg1uom: this.selectedSubPkg1 !== null ? this.selectedSubPkg1['Package Code'] : '',
          subpkg2qty: this.subpkg2qty !== 0 ? this.subpkg2qty : 0,
          subpkg2uom: this.selectedSubPkg2 !== null ? this.selectedSubPkg2['Package Code'] : '',
          subpkg3qty: this.subpkg3qty !== 0 ? this.subpkg3qty : 0,
          subpkg3uom: this.selectedSubPkg3 !== null ? this.selectedSubPkg3['Package Code'] : '',
          qtyperpallet: this.pallet !== '' ? parseInt(this.pallet) : 0,
          prodownercustomer: customersToSend
            .filter((obj) => obj.hasOwnProperty('custaction'))
            .map((customer) => {
              return {
                custaction: customer['custaction'],
                id: this.warehouseAction === 'New' ? '' : parseInt(this.prodID),
                txnid: customer['TxnID'],
                custcode: customer['Cust Code'],
                custprodcode1: customer['Cust Prod Code 1'],
                custprodname: customer['Cust Prod Name'],
                issuemethod: customer['Issue Method'],
              };
            }),
          prodownersupplier: suppliersToSend
            .filter((obj) => obj.hasOwnProperty('suppaction'))
            .map((supplier) => {
              return {
                suppaction: supplier['suppaction'],
                id: this.warehouseAction === 'New' ? '' : parseInt(this.prodID),
                txnid: supplier['TxnID'],
                suppcode: supplier['Supp Code'],
                suppprodcode1: supplier['Supp Prod Code 1'],
                suppprodname: supplier['Supp Prod Name'],
              };
            }),
          whproductcomposition: compositionsToSend
            .filter((obj) => obj.hasOwnProperty('subaction'))
            .map((composition) => {
              return {
                subaction: composition['subaction'],
                id: this.warehouseAction === 'New' ? '' : parseInt(this.prodID),
                seqno: composition['SeqNo'] === undefined ? '' : composition['SeqNo'],
                subproductid: parseInt(composition['PProdCode']),
                txnid: composition['TxnID'],
                subproductcode: composition['Sub Product Code'],
                qty: parseInt(composition['Qty']),
                pprodcode: composition['PProdCode'],
                cocode: '',
              };
            }),

          prodattachment: this.attachmentstoSend.map((item) => {
            return {
              attachmentaction: item['attachmentaction'],
              id: this.warehouseAction === 'New' ? '' : parseInt(this.prodID),
              seqno: item['attachmentaction'] === 'D' ? item['SeqNo'].toString() : '',
              filename: item['FileName'],
              attachment: item['Attachment'],
              remark: item['Remarks'],
            };
          }),
        };

        this.$fullscreenLoading('Saving Warehouse Product');
        let save = await this.$apiCallV2('/WarehouseProduct/SaveWHProduct', dataToPost);
        this.$fullscreenLoading();
        if (save[0].Results == 0) {
          this.entrySuccess.push(save[0].ErrMsg);
          this.viewMode = true;
          this.popupEntryTitle = 'View Warehouse Product';
          this.$nextTick(async () => await this.getWarehouseProducts());
          this.selectedWarehouseProduct = [];
          this.refreshCustomers(this.customers);
          this.refreshSuppliers(this.suppliers);
          this.refreshCompositions(this.compositions);
          return;
        }
        this.entryError.push(save[0].ErrMsg);
      }
    },

    populatePopupEntryFields: async function (warehouseDetails) {
      this.updatedBy = `${warehouseDetails['WHProduct']['Updated By'] || ''} ${
        warehouseDetails['WHProduct']['Last Update'] ?
          new Date(warehouseDetails['WHProduct']['Last Update']).formatString('dd/MM/yyyy HH:mm')
        : ''
      }`;
      this.createdBy = `${warehouseDetails['WHProduct']['Created By'] || ''} ${
        warehouseDetails['WHProduct']['Created On'] ?
          new Date(warehouseDetails['WHProduct']['Created On']).formatString('dd/MM/yyyy HH:mm')
        : ''
      }`;

      this.prodID = warehouseDetails['WHProduct']['ID'];
      this.prodCode = warehouseDetails['WHProduct']['Product Code'];
      this.prodName = warehouseDetails['WHProduct']['Product Name'];
      this.selectedItemType = this.itemType.getSelected(warehouseDetails['WHProduct']['Item Type'], 'Item Type');
      this.selectedProductOrigin = this.productOrigin.getSelected(
        warehouseDetails['WHProduct']['Origin'],
        'Country Code',
      );
      this.selectedMetric = this.metric.getSelected(warehouseDetails['WHProduct']['Metric'], 'UOM Code');
      this.gross = warehouseDetails['WHProduct']['Gross Weight'];
      this.nett = warehouseDetails['WHProduct']['Nett Weight'];
      this.length = warehouseDetails['WHProduct']['Length'];
      this.width = warehouseDetails['WHProduct']['Width'];
      this.height = warehouseDetails['WHProduct']['Height'];
      this.volume = warehouseDetails['WHProduct']['Volume'];
      this.gsm = warehouseDetails['WHProduct']['GSM'];
      this.grade = warehouseDetails['WHProduct']['Grade'];
      this.shape = warehouseDetails['WHProduct']['Shade'];
      this.size = warehouseDetails['WHProduct']['Size'];
      this.color = warehouseDetails['WHProduct']['Colour'];
      this.cusPcode = warehouseDetails['WHProduct']['Cust Prod Code'];
      this.supPcode = warehouseDetails['WHProduct']['Supp Prod Code'];
      this.selectedSupCode = this.supplierCode.getSelected(
        warehouseDetails['WHProduct']['Supplier Code'],
        'Supplier Code',
      );
      this.selectedDuration = this.duration.getSelected(
        warehouseDetails['WHProduct']['Shelf Life Duration'],
        'Description',
      );
      this.shelf = warehouseDetails['WHProduct']['Shelf Life'];
      this.stockable = warehouseDetails['WHProduct']['Stockable'] === 'Yes' ? true : false;

      this.reorder = warehouseDetails['WHProduct']['Reorder Level'];
      this.minStock = warehouseDetails['WHProduct']['Min Stock Level'];
      this.prodClass = warehouseDetails['WHProduct']['Product Class'];
      this.abcClass = warehouseDetails['WHProduct']['ABC Class'];
      this.abcCode = warehouseDetails['WHProduct']['ABC Code'];
      this.selectedBarcode = this.barcode.getSelected(
        warehouseDetails['WHProduct']['Barcode Ref'],
        'BarCode Reference',
      );
      this.unit = warehouseDetails['WHProduct']['Unit Price'];
      this.sell = warehouseDetails['WHProduct']['Selling Price'];
      this.cost = warehouseDetails['WHProduct']['Cost Method'];
      this.price = warehouseDetails['WHProduct']['Price Category'];
      this.disc = warehouseDetails['WHProduct']['Discount'];
      this.bill = warehouseDetails['WHProduct']['Bill of Material'];
      this.specificationValue = warehouseDetails['WHProduct']['Specification'];
      this.selectedGLAccCode = this.GLAccCode.getSelected(warehouseDetails['WHProduct']['GL Code'], 'Acc No');
      this.selectedGSTARTaxCode = this.GSTARTaxCode.getSelected(
        warehouseDetails['WHProduct']['GST AR Tax Code'],
        'Tax Code',
      );
      this.selectedARGLAccNo = this.GLAccCode.getSelected(warehouseDetails['WHProduct']['GST AR GL Code'], 'Acc No');
      this.selectedAPTaxCode = this.APTaxCode.getSelected(warehouseDetails['WHProduct']['GST AP Tax Code'], 'Tax Code');
      this.selectedAPGLAccCode = this.GLAccCode.getSelected(warehouseDetails['WHProduct']['GST AP GL Code'], 'Acc No');
      this.handlingInstructionValue = warehouseDetails['WHProduct']['Handling Instruction'];
      this.selectedStackable = {
        label: warehouseDetails['WHProduct']['Stackable'],
      };
      this.minTemp = warehouseDetails['WHProduct']['Min Temp'];
      this.maxTemp = warehouseDetails['WHProduct']['Max Temp'];
      this.optTemp = warehouseDetails['WHProduct']['Opt Temp'];
      this.remarksValue = warehouseDetails['WHProduct']['Remarks'];
      this.selectedBunch = this.subPkg.getSelected(warehouseDetails['WHProduct']['UOM'], 'Package Code');
      this.selectedLoose = {
        label: warehouseDetails['WHProduct']['Loose Packing'],
      };
      this.selectedSub = warehouseDetails['WHProduct']['Subpackage'] === 'Yes' ? true : false;
      this.subpkg1qty =
        warehouseDetails['WHProduct']['Subpkg1 Qty'] === '0' ? '' : warehouseDetails['WHProduct']['Subpkg1 Qty'];
      this.selectedSubPkg1 = this.subPkg.getSelected(warehouseDetails['WHProduct']['Subpkg1 UOM'], 'Package Code');
      this.subpkg2qty =
        warehouseDetails['WHProduct']['Subpkg2 Qty'] === '0' ? '' : warehouseDetails['WHProduct']['Subpkg2 Qty'];
      this.selectedSubPkg2 = this.subPkg.getSelected(warehouseDetails['WHProduct']['Subpkg2 UOM'], 'Package Code');
      this.subpkg3qty =
        warehouseDetails['WHProduct']['Subpkg3 Qty'] === '0' ? '' : warehouseDetails['WHProduct']['Subpkg3 Qty'];
      this.selectedSubPkg3 = this.subPkg.getSelected(warehouseDetails['WHProduct']['Subpkg3 UOM'], 'Package Code');
      this.pallet = warehouseDetails['WHProduct']['Qty per Pallet'];
      this.selectedSubProdID = warehouseDetails['WHProduct']['WHProductComposition']?.['Sub Product Id'];
      this.subQty = warehouseDetails['WHProduct']['WHProductComposition']?.['Qty'];

      this.populateAttachments(warehouseDetails['WHProduct']['ProdAttachment']);
      this.populateCustomers(warehouseDetails['WHProduct']['ProdOwnerCustomer']);
      this.populateSuppliers(warehouseDetails['WHProduct']['ProdOwnerSupplier']);
      this.populateCompositions(warehouseDetails['WHProduct']['WHProductComposition']);

      console.log(warehouseDetails['WHProduct']);
    },

    populateCustomers: function (customers) {
      if (!customers) return;
      this.customers.push(...customers);
    },

    populateSuppliers: function (suppliers) {
      if (!suppliers) return;
      this.suppliers.push(...suppliers);
    },
    populateCompositions: function (compositions) {
      if (!compositions) return;
      this.compositions.push(...compositions);
    },

    populateAttachments: function (attachments) {
      if (!attachments) return;

      attachments.forEach((attachment) => {
        let attachmentEntry = {
          attachmentaction: '',
          SeqNo: attachment['SeqNo'],
          ID: attachment['ID'],
          FileName: attachment['File Name'],
          Remarks: attachment['Remark'],
          Attachment: attachment['Attachment'],
        };
        this.attachments.push(attachmentEntry);
        this.existingAttachments.push(attachmentEntry);
      });
    },

    addAttachment: function (addedAttachments) {
      this.attachmentstoSend.push(...addedAttachments);
    },
    deleteAttachment: function (deletedAttachments) {
      deletedAttachments.forEach((file) => {
        let match = this.existingAttachments.filter((existingFile) => {
          return existingFile['FileName'] === file['FileName'];
        });
        if (match.length > 0) {
          match.forEach((item) => {
            item.attachmentaction = 'D';
          });
          this.attachmentstoSend.push(...match);
        }
      });
    },

    clearPopupEntryFields: async function () {
      this.createdBy = '';
      this.updatedBy = '';
      this.lastUpdate = '';
      this.prodID = '******';
      this.prodCode = '';
      this.prodName = '';
      this.selectedItemType = null;
      this.selectedProductOrigin = null;
      this.selectedMetric = null;
      this.gross = '';
      this.nett = '';
      this.length = '';
      this.width = '';
      this.height = '';

      this.volume = '';
      this.gsm = '';
      this.grade = '';
      this.shape = '';
      this.size = '';
      this.color = '';
      this.cusPcode = '';
      this.supPcode = '';
      this.selectedSupCode = null;
      this.selectedDuration = null;
      this.stackable = null;
      this.reorder = '';
      this.minStock = '';
      this.prodClass = '';
      this.abcClass = '';
      this.abcCode = '';
      this.selectedBarcode = null;
      this.selectedBunch = null;
      this.unit = '';
      this.sell = '';
      this.cost = '';
      this.price = '';
      this.disc = '';
      this.bill = '';
      this.specificationValue = '';
      this.selectedGLAccCode = null;
      this.selectedGSTARTaxCode = null;
      this.selectedARGLAccNo = null;
      this.selectedAPTaxCode = null;
      this.selectedAPGLAccCode = null;
      this.handlingInstructionValue = '';

      this.minTemp = '';
      this.maxTemp = '';
      this.optTemp = '';
      this.remarksValue = '';
      this.selectedCustomerCode = null;
      this.custProdCode = '';
      this.custProdName = '';
      this.selectedIssueMethod = null;
      this.selectedSupplierData = null;
      this.supProdCode = '';
      this.supProdName = '';
      this.selectedLoose = null;
      this.selectedSub = false;
      this.selectedSubPkg1 = null;
      this.subpkg1qty = '';
      this.selectedSubPkg2 = null;
      this.subpkg2qty = '';
      this.selectedSubPkg3 = null;
      this.subpkg3qty = '';

      this.subpkg3qty = '';
      this.pallet = '';

      this.subQty = '';
      this.attachmentstoSend = [];
      this.viewMode = false;
      this.tabIndex = 0;
      this.CustomerData = [];
      this.supplierDetails = [];
      this.compositionData = [];
      this.attachments = [];
      this.stockable = false;

      this.showCustDetails = false;
      this.showSuppDetails = false;
      this.showCompDetails = false;

      this.itemTypeSearch = '';
      this.productOriginSearch = '';
      this.supCodeSearch = '';
      this.barcodeSearch = '';

      this.gLAccCodeSearch = '';
      this.gSTARTaxCodeSearch = '';
      this.aRGLAccNoSearch = '';
      this.aPTaxCodeSearch = '';
      this.aPGLAccCodeSearch = '';

      this.subPkg1Search = '';
      this.subPkg2Search = '';
      this.subPkg3Search = '';
      this.bunchSearch = '';
      this.metricSearch = '';
      this.durationSearch = '';

      this.selectedLoose = null;
      this.selectedStackable = null;
      this.attachmentstoSend = [];
      this.customers = [];
      this.deletedCustomers = [];
      this.suppliers = [];
      this.deletedSuppliers = [];
      this.compositions = [];
      this.deletedCompositions = [];

      this.selectedCustomers = [];
      this.selectedSupplier = [];
      this.selectedCompositions = [];

      this.clearMessages();
    },

    clearMessages: function () {
      this.entryMessages = [];
      this.entryError = [];
      this.entryRequired = [];
      this.entrySuccess = [];
    },

    clearErrors: function () {
      this.prodIDError = '';
      this.prodCodeError = '';
      this.prodNameError = '';
      this.shapeError = '';
      this.sizeError = '';
      this.bunchError = '';
      this.prodIDError = '';
      this.prodCodeError = '';
      this.prodNameError = '';
      this.shapeError = '';
      this.sizeError = '';
      this.bunchError = '';
      this.itemTypeError = '';
      this.productOriginError = '';
      this.metricError = '';
      this.supplierCodeError = '';
      this.durationError = '';
      this.barcodeError = '';
      this.gLAccCodeError = '';
      this.gSTARTaxCodeError = '';
      this.aRGLAccNoError = '';
      this.aPTaxCodeError = '';
      this.aPGLAccCodeError = '';
      this.subPkg1Error = '';
      this.subPkg2Error = '';
      this.subPkg3Error = '';
      this.subPkg1QtyError = '';
      this.subPkg2QtyError = '';
      this.subPkg3QtyError = '';
      this.discError = '';
    },

    checkFields: function () {
      this.clearMessages();

      if (!this.prodCode || this.prodCode.trim().length === 0) {
        this.prodCodeError = 'Product Code is required!';
        this.entryRequired.push({
          key: 'productCode',
          value: this.prodCodeError,
        });
      }
      if (!this.prodName || this.prodName.trim().length === 0) {
        this.prodNameError = 'Product Name is required!';
        this.entryRequired.push({
          key: 'productName',
          value: this.prodNameError,
        });
      }

      if (!this.shape || this.shape.trim().length === 0) {
        this.shapeError = 'Shade is required!';
        this.entryRequired.push({
          key: 'shape',
          value: this.shapeError,
        });
      }
      if (!this.size || this.size.trim().length === 0) {
        this.sizeError = 'Size is required!';
        this.entryRequired.push({
          key: 'size',
          value: this.sizeError,
        });
      }
      if (!this.selectedBunch) {
        this.bunchError = 'UOM is required!';
        this.entryRequired.push({
          key: 'bunch',
          value: this.bunchError,
        });
      }

      if (!this.selectedBunch && this.bunchSearch !== '') {
        this.bunchError = 'Invalid UOM!';
        this.entryRequired.push({
          key: 'bunch',
          value: this.bunchError,
        });
      }

      // Check if the user enters data other from the ones from the query selector
      if (!this.selectedItemType && this.itemTypeSearch !== '') {
        this.itemTypeError = 'Invalid Item Type!';
        this.entryRequired.push({
          key: 'itemType',
          value: this.itemTypeError,
        });
      }

      if (!this.selectedProductOrigin && this.productOriginSearch !== '') {
        this.productOriginError = 'Invalid Product Origin!';
        this.entryRequired.push({
          key: 'productOrigin',
          value: this.productOriginError,
        });
      }

      if (!this.selectedMetric && this.metricSearch !== '') {
        this.metricError = 'Invalid Metric';
        this.entryRequired.push({
          key: 'metric',
          value: this.metricError,
        });
      }

      if (!this.selectedSupCode && this.supCodeSearch !== '') {
        this.supplierCodeError = 'Invalid Supplier Code!';
        this.entryRequired.push({
          key: 'supplierCode',
          value: this.supplierCodeError,
        });
      }

      if (!this.selectedDuration && this.durationSearch !== '') {
        this.durationError = 'Invalid Duration!';
        this.entryRequired.push({
          key: 'duration',
          value: this.durationError,
        });
      }

      if (!this.selectedBarcode && this.barcodeSearch !== '') {
        this.barcodeError = 'Invalid Barcode!';
        this.entryRequired.push({
          key: 'barcode',
          value: this.barcodeError,
        });
      }

      if (!this.selectedGLAccCode && this.gLAccCodeSearch !== '') {
        this.gLAccCodeError = 'Invalid GL Acc Code ';
        this.entryRequired.push({
          key: 'GLAccCode',
          value: this.gLAccCodeError,
        });
      }

      if (!this.selectedGSTARTaxCode && this.gSTARTaxCodeSearch !== '') {
        this.gSTARTaxCodeError = 'Invalid GST AR Tax Code ';
        this.entryRequired.push({
          key: 'GSTARTaxCode',
          value: this.gSTARTaxCodeError,
        });
      }

      if (!this.selectedARGLAccNo && this.aRGLAccNoSearch !== '') {
        this.aRGLAccNoError = 'Invalid GST AR GL Acc No ';
        this.entryRequired.push({
          key: 'ARGLAccNo',
          value: this.aRGLAccNoError,
        });
      }

      if (!this.selectedAPTaxCode && this.aPTaxCodeSearch !== '') {
        this.aPTaxCodeError = 'Invalid GST AP Tax Code';
        this.entryRequired.push({
          key: 'APTaxCode',
          value: this.aPTaxCodeError,
        });
      }

      if (!this.selectedAPGLAccCode && this.aPGLAccCodeSearch !== '') {
        this.aPGLAccCodeError = 'Invalid GST AP GL Acc No';
        this.entryRequired.push({
          key: 'APGLAccCode',
          value: this.aPGLAccCodeError,
        });
      }

      if (!this.selectedSubPkg1 && this.subPkg1Search !== '') {
        this.subPkg1Error = 'Invalid Sub Package 1';
        this.entryRequired.push({
          key: 'subpkg1',
          value: this.subPkg1Error,
        });
      }

      if (!this.selectedSubPkg2 && this.subPkg2Search !== '') {
        this.subPkg2Error = 'Invalid Sub Package 2';
        this.entryRequired.push({
          key: 'subpkg2',
          value: this.subPkg2Error,
        });
      }

      if (!this.selectedSubPkg3 && this.subPkg3Search !== '') {
        this.subPkg3Error = 'Invalid Sub Package 3';
        this.entryRequired.push({
          key: 'subpkg3',
          value: this.subPkg3Error,
        });
      }

      if (!this.selectedSubPkg1 && this.subpkg1qty !== '') {
        this.subPkg1Error = 'UOM is Required';
        this.entryRequired.push({
          key: 'subpkg1',
          value: this.subPkg1Error,
        });
      }

      if (this.selectedSubPkg1 && this.subpkg1qty === '') {
        this.subPkg1QtyError = 'Quantity is Required';
        this.entryRequired.push({
          key: 'subPkgQuantity1',
          value: this.subPkg1QtyError,
        });
      }

      if (!this.selectedSubPkg2 && this.subpkg2qty !== '') {
        this.subPkg2Error = 'UOM is Required';
        this.entryRequired.push({
          key: 'subpkg2',
          value: this.subPkg2Error,
        });
      }

      if (this.selectedSubPkg2 && this.subpkg2qty === '') {
        this.subPkg2QtyError = 'Quantity is Required';
        this.entryRequired.push({
          key: 'subPkgQuantity2',
          value: this.subPkg2QtyError,
        });
      }
      if (!this.selectedSubPkg3 && this.subpkg3qty !== '') {
        this.subPkg3Error = 'UOM is Required';
        this.entryRequired.push({
          key: 'subpkg3',
          value: this.subPkg3Error,
        });
      }

      if (this.selectedSubPkg3 && this.subpkg3qty === '') {
        this.subPkg3QtyError = 'Quantity is Required';
        this.entryRequired.push({
          key: 'subPkgQuantity3',
          value: this.subPkg3QtyError,
        });
      }

      if (this.disc && this.disc > 100) {
        this.discError = 'Discount cannot exceed 100%!';
        this.entryRequired.push({
          key: 'disc',
          value: this.discError,
        });
      }

      return this.entryRequired.length > 0; // return true if there are errors
    },

    entryRequiredClicked: function (item) {
      let errorTab = this.errorMapping.find((e) => e['key'] == item);

      if (errorTab) {
        this.currentTab = errorTab['tab'];
      }

      this.$nextTick(() => {
        this.$refs[item].setFocus();
      });
    },

    menuClicked: async function (menuButton) {
      this.clearMessages();
      this.clearErrors();
      this.clearPopupEntryFields();
      this.currentTab = 0;
      switch (menuButton['title']) {
        case 'New Warehouse Product':
          this.warehouseAction = 'New';
          this.popupEntryTitle = 'New Warehouse Product';
          this.viewMode = false;
          this.popupEntryShow = true;
          break;

        case 'Edit Warehouse Product':
          if (this.selectedWarehouseProduct.length == 0) {
            this.$showAlert('error', 'No Warehouse Product Selected', 'Please select a Warehouse Product to edit', [
              { text: 'OK' },
            ]);
            return;
          }

          let product = await this.getProductDetails(this.selectedWarehouseProduct[0]['Product ID']);
          this.populatePopupEntryFields(product);
          this.warehouseAction = 'Edit';
          this.popupEntryTitle = `Edit Warehouse Product`;
          this.viewMode = false;
          this.popupEntryShow = true;
          break;

        case 'Delete Warehouse Product':
          if (this.selectedWarehouseProduct.length == 0) {
            this.$showAlert('error', 'No Warehouse Product Selected', 'Please select a Warehouse Product to delete', [
              { text: 'OK' },
            ]);
            return;
          }

          if (
            (await this.$showAlert(
              'warning',
              'Are you sure you want to delete this Warehouse Product?',
              `Product Code: ${this.selectedWarehouseProduct[0]['Product ID']}<br>` +
                `Description: ${this.selectedWarehouseProduct[0]['Description']}`,
              [
                { text: 'Delete', color: 'red', textColor: 'white' },
                { text: 'Cancel', color: 'lightgray' },
              ],
            )) == 'Cancel' // user clicked Cancel
          ) {
            return; // exit function
          }

          this.$fullscreenLoading('Deleting Warehouse Product');
          let result = await this.$apiCallV2('/WarehouseProduct/SaveWHProduct', {
            action: 'D',
            id: this.selectedWarehouseProduct[0]['Product ID'],
            productcode: this.selectedWarehouseProduct[0]['Product Code'],
          });
          this.$fullscreenLoading();

          if (result[0]['Results'] == 0) {
            // successfully deleted
            this.$showAlert(
              'success',
              'Warehouse Product Deleted',
              `Warehouse Product ID ${this.selectedWarehouseProduct[0]['Product ID']} has been successfully deleted`,
              [{ text: 'OK' }],
            );
            this.$nextTick(async () => await this.getWarehouseProducts());
            this.selectedWarehouseProduct = [];
            return;
          }
          // an error occurred
          this.$showAlert('error', 'Warehouse Product Not Deleted', result['ErrMsg'], [{ text: 'OK' }]);
          break;

        case 'View Warehouse Product':
          if (this.selectedWarehouseProduct.length == 0) {
            this.$showAlert('error', 'No Warehouse Product Selected', 'Please select a Warehouse Product to view', [
              { text: 'OK' },
            ]);
            return;
          }

          product = await this.getProductDetails(this.selectedWarehouseProduct[0]['Product ID']);
          this.populatePopupEntryFields(product);

          this.showCustDetails = false;
          this.showSuppDetails = false;
          this.showCompDetails = false;
          this.warehouseAction = 'View';
          this.popupEntryTitle = 'View Warehouse Product';
          this.viewMode = true;
          this.popupEntryShow = true;

          break;
      }
    },

    checkSubPackageRow: function (string, object, row) {
      if (string !== '' && object !== null) {
        if (row === 'row1') {
          this.subpackageRow2 = false;
        } else if (row === 'row2') {
          this.subpackageRow3 = false;
        }
      } else {
        if (row === 'row1') {
          this.subpackageRow2 = true;
          this.subpkg2qty = '';
          this.selectedSubPkg2 = null;
        } else if (row === 'row2') {
          this.subpackageRow3 = true;
          this.subpkg3qty = '';
          this.selectedSubPkg3 = null;
        }
      }
    },
    getWarehouseProducts: async function () {
      this.warehouseData = [];
      this.warehouseData = await this.$apiCallV2('/Master/GetListMaster', {
        masterfile: 'WHPRODUCT',
        module: 'MASTER',
      });
      this.$fullscreenLoading();
    },
  },

  async mounted() {
    this.$bus.$on('pageSASControlAccess', (x) => {
      if (x.page == 'Warehouse Product') {
        x.data.forEach((k) => {
          //key in menu same as control value from api
          let selectedMenu = this.menus.find((m) => m['key'] == k['Control']);
          if (selectedMenu) {
            selectedMenu[k['Properties'].toLowerCase()] = k['PropertiesValue'] == 'True';
          }
        });
      }
    });

    this.$fullscreenLoading('Getting Warehouse Products');
    this.getWarehouseProducts();

    this.itemType = await this.$apiCallV2('/Master/GetListMaster', {
      masterfile: 'itemtype',
      module: 'MASTER',
    });

    this.productOrigin = await this.$apiCallV2('/Master/GetListMaster', {
      masterfile: 'country',
      module: 'MASTER',
    });

    this.metric = await this.$apiCallV2('/Master/GetListMaster', {
      masterfile: 'uom',
      module: 'MASTER',
    });

    this.supplierCode = await this.$apiCallV2('/Master/GetListMaster', {
      masterfile: 'supplier',
      module: 'MASTER',
    });

    this.duration = await this.$apiCallV2('/Master/GetListMaster', {
      masterfile: 'SHELFLIFE',
      module: 'MASTER',
    });

    this.barcode = (
      await this.$apiCallV2('/Master/GetListMaster', {
        masterfile: 'BARCODEREF',
        module: 'MASTER',
      })
    ).map((barcode) => ({
      ...barcode,
      'Barcode Ref - Type': `${barcode['BarCode Reference']} - ${barcode['Type']}`,
    }));

    this.GLAccCode = await this.$apiCallV2('/Master/GetListMaster', {
      masterfile: 'glaccount',
      module: 'MASTER',
    });

    this.GSTARTaxCode = await this.$apiCallV2('/Master/GetListMaster', {
      masterfile: 'artaxcode',
      module: 'MASTER',
    });

    this.ARGLAccNo = [...this.GLAccCode];

    this.APTaxCode = await this.$apiCallV2('/Master/GetListMaster', {
      masterfile: 'aptaxcode',
      module: 'MASTER',
    });

    this.APGLAccCode = [...this.GLAccCode];

    this.customerCode = await this.$apiCallV2('/Master/GetListMaster', {
      masterfile: 'customer',
      module: 'MASTER',
    });

    this.issueMethod = await this.$apiCallV2('/Master/GetListMaster', {
      masterfile: 'ROTATIONRULE',
      module: 'MASTER',
    });

    this.supplierData = await this.$apiCallV2('/Master/GetListMaster', {
      masterfile: 'supplier',
      module: 'MASTER',
    });

    this.subPkg = await this.$apiCallV2('/Master/GetListMaster', {
      masterfile: 'WHPACKAGE',
      module: 'MASTER',
    });

    this.subProdIDData = await this.$apiCallV2('/Master/GetListMaster', {
      masterfile: 'WHPRODUCTBYID',
      module: 'MASTER',
    });

    this.$fullscreenLoading();
  },

  watch: {
    selectedSub: function (newValue, oldValue) {
      if (newValue === true) {
        this.subpackageRow1 = false;
      } else {
        // disable the input
        this.subpackageRow1 = true;
        this.subpackageRow2 = true;
        this.subpackageRow3 = true;
        // clear the entries
        this.subpkg1qty = '';
        this.selectedSubPkg1 = null;
        this.subpkg2qty = '';
        this.selectedSubPkg2 = null;
        this.subpkg3qty = '';
        this.selectedSubPkg3 = null;
        // clear the errors
        this.subPkg1Error = '';
        this.subPkg1QtyError = '';
        this.subPkg2Error = '';
        this.subPkg2QtyError = '';
        this.subPkg3Error = '';
        this.subPkg3QtyError = '';
      }
    },
    subpkg1qty: function (newValue) {
      this.checkSubPackageRow(newValue, this.selectedSubPkg1, 'row1');
    },
    selectedSubPkg1: function (newValue) {
      this.checkSubPackageRow(this.subpkg1qty, newValue, 'row1');
    },
    subpkg2qty: function (newValue) {
      this.checkSubPackageRow(newValue, this.selectedSubPkg2, 'row2');
    },
    selectedSubPkg2: function (newValue) {
      this.checkSubPackageRow(this.subpkg2qty, newValue, 'row2');
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/css/all.scss';

.warehouse-main {
  width: 100%;
  height: 100%;
  display: flex;
  padding: 10px 45px;
  font-size: 12px;
}

.right-container {
  width: 100%;
  overflow: hidden;
  height: 100%;
  padding: 5px;
  border-radius: 5px;
  margin-left: 5px;
  background-color: white;
}

.rowBox {
  border: none;
  outline: none;
  width: 100%;
  height: auto;
  display: flex;
  align-items: top;
  justify-content: flex-start;
  transition: 0.3s;
  flex-direction: row;
}

.columnBox {
  width: 100%;
  height: auto;
  display: flex;
  align-items: right;
  flex-direction: column;
  justify-content: flex-start;
  transition: 0.3s;
}

#composition {
  height: 100%;
}

#priceColumn1 {
  margin-right: 3px;
}

#priceColumn2 {
  margin-left: 3px;
}

#checkbox {
  width: 100%;
}

#supplier {
  margin-top: 50px;
}
</style>
